import React from 'react';
import { InfoCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import { blue } from '@ant-design/colors';
import { ComplianceCheckStatus } from '__generated__/graphql';
import ComplianceCheckStatusBanner, {
  ComplianceCheckBannerContent,
} from 'components/ComplianceCheckStatusBanner';

export interface PgaDisclaimAiStatusProps {
  status?: ComplianceCheckStatus | null;
  // Agency code shown in the copy (e.g. "EPA", "FDA").
  agencyName: string;
  // Whether the flag is actually disclaimed (disclaim set / disclaimer code
  // present). Drives the AI_VALIDATED copy: a validated AI verdict can be a
  // "yes, disclaim" or a "no, do not disclaim", so the banner must not assert a
  // disclaim happened unless it actually did.
  isDisclaimed?: boolean;
  // Placeholder until the model returns a rationale.
  rationale?: string;
}

// TODO: replace with the actual rationale from the AI response once available.
const RATIONALE_PLACEHOLDER = '<placeholder>';

// antd info blue — keeps the FAILED icon blue on the red error banner.
const INFO_ICON_COLOR = blue.primary;

const PgaDisclaimAiStatus: React.FC<PgaDisclaimAiStatusProps> = ({
  status,
  agencyName,
  isDisclaimed = false,
  rationale,
}) => {
  const contentByStatus: Partial<
    Record<ComplianceCheckStatus, ComplianceCheckBannerContent>
  > = {
    // Filled icons: antd shows outlined ones when a description is present.
    [ComplianceCheckStatus.IN_PROGRESS]: {
      type: 'info',
      icon: <InfoCircleFilled />,
      message: `Checking ${agencyName} requirements`,
      description: `AI is analyzing the HTS code to determine whether this ${agencyName} requirement can be disclaimed. This usually takes 1–5 minutes, and any disclaim will be applied automatically.`,
    },
    // A validated AI verdict (AI_VALIDATED) can be a "yes, disclaim" or a "no,
    // do not disclaim" — so the copy branches on the actual disclaim decision
    // (isDisclaimed) rather than asserting a disclaim happened. COMPLETED (AI
    // evaluated but not eligible) and USER_MANUAL_OVERRIDE (the operator
    // manually edited the flag, replacing the AI disclaim) are intentionally
    // unmapped — neither may claim an AI verdict, so both render no banner.
    [ComplianceCheckStatus.AI_VALIDATED]: isDisclaimed
      ? {
          type: 'success',
          icon: <CheckCircleFilled />,
          message: `${agencyName} requirement disclaimed by AI`,
          description: (
            <>
              This {agencyName} requirement has been disclaimed by AI based on
              its analysis of the HTS code. You remain solely responsible for
              ensuring the accuracy of this determination.
              <br />
              <strong>Rationale:</strong> {rationale || RATIONALE_PLACEHOLDER}
            </>
          ),
        }
      : {
          type: 'info',
          icon: <InfoCircleFilled />,
          message: `${agencyName} requirement not disclaimed by AI`,
          description: (
            <>
              AI analyzed the HTS code and determined this {agencyName}{' '}
              requirement cannot be disclaimed, so it applies to this line. You
              remain solely responsible for ensuring the accuracy of this
              determination.
              <br />
              <strong>Rationale:</strong> {rationale || RATIONALE_PLACEHOLDER}
            </>
          ),
        },
    [ComplianceCheckStatus.FAILED]: {
      type: 'error',
      icon: <InfoCircleFilled style={{ color: INFO_ICON_COLOR }} />,
      message: `Manual review required for ${agencyName} disclaim`,
      description: `AI was unable to determine whether this ${agencyName} requirement can be disclaimed. Please review the information and decide on whether the item can be disclaimed.`,
    },
  };

  return (
    <ComplianceCheckStatusBanner
      status={status}
      contentByStatus={contentByStatus}
    />
  );
};

export default PgaDisclaimAiStatus;
